<template>
  <div class="app-container warehouse-summary">
    <div class="page-header">
      <div class="page-header__left">
        <h2 class="page-header__title">委外库存汇总</h2>
        <p class="page-header__desc">按物料、规格汇总当前委外商协同库存</p>
      </div>
      <div class="page-header__actions">
        <el-button type="primary" size="small" :loading="loading" @click="refreshAll">
          刷新
        </el-button>
        <el-button size="small" @click="goOverview">库存总览</el-button>
        <el-button size="small" @click="goWarehouse">库存明细</el-button>
        <el-button size="small" @click="goFlow">库存流水</el-button>
        <el-button size="small" @click="goBatchInventory">批次台账</el-button>
      </div>
    </div>

    <div class="stat-panel" v-loading="statLoading">
      <el-row :gutter="16">
        <el-col
          v-for="item in statCards"
          :key="item.key"
          :xs="24"
          :sm="12"
          :md="6"
        >
          <div class="stat-card" :class="'stat-card--' + item.tone">
            <div
              class="stat-card__accent"
              :style="{ background: item.accent }"
            />
            <div class="stat-card__body">
              <div class="stat-card__title-row">
                <span class="stat-card__title">{{ item.title }}</span>
              </div>
              <div class="stat-card__count" :style="{ color: item.color }">
                {{ item.count }}
              </div>
              <div class="stat-card__desc">{{ item.desc }}</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <el-row :gutter="16" class="structure-row">
      <el-col :xs="24" :md="16">
        <div class="chart-panel" v-loading="chartLoading">
          <div class="chart-panel__head">
            <div>
              <div class="chart-panel__eyebrow">库存结构</div>
              <div class="chart-panel__title">按物料汇总</div>
            </div>
          </div>
          <div
            v-if="materialRows.length"
            ref="materialChart"
            class="chart-panel__body"
            :style="{ height: chartHeight + 'px' }"
          />
          <div v-else class="chart-panel__empty">暂无物料汇总数据</div>
          <div v-if="materialTotal > listQuery.pageSize" class="chart-panel__pager">
            <pagination
              :total="materialTotal"
              :page.sync="listQuery.pageNum"
              :limit.sync="listQuery.pageSize"
              :page-sizes="[10, 20, 50]"
              style="text-align: right"
              @pagination="loadMaterialSummary"
            />
          </div>
        </div>
      </el-col>

      <el-col :xs="24" :md="8">
        <div class="structure-panel" v-loading="statLoading">
          <div class="chart-panel__head">
            <div>
              <div class="chart-panel__eyebrow">结构解读</div>
              <div class="chart-panel__title">库存构成</div>
            </div>
          </div>

          <div class="structure-block">
            <div class="structure-block__title">产品委外 vs 工序委外</div>
            <div
              v-for="row in structure.bizKindRows"
              :key="row.key"
              class="progress-row"
            >
              <div class="progress-row__meta">
                <span>
                  <i class="progress-row__dot" :style="{ background: row.color }" />
                  {{ row.label }}
                </span>
                <span class="progress-row__value">
                  {{ formatQty(row.value) }}（{{ row.ratio }}%）
                </span>
              </div>
              <el-progress
                :percentage="row.ratio"
                :stroke-width="10"
                :color="row.color"
                :show-text="false"
              />
            </div>
          </div>

          <div class="structure-block">
            <div class="structure-block__title">有批次 vs 无批次</div>
            <div
              v-for="row in structure.batchRows"
              :key="row.key"
              class="progress-row"
            >
              <div class="progress-row__meta">
                <span>
                  <i class="progress-row__dot" :style="{ background: row.color }" />
                  {{ row.label }}
                </span>
                <span class="progress-row__value">
                  {{ formatQty(row.value) }}（{{ row.ratio }}%）
                </span>
              </div>
              <el-progress
                :percentage="row.ratio"
                :stroke-width="10"
                :color="row.color"
                :show-text="false"
              />
            </div>
          </div>

          <div class="structure-block structure-block--donut">
            <div class="structure-block__title">类型 × 批次构成</div>
            <div ref="structureDonut" class="structure-donut" />
            <div class="structure-legend">
              <div
                v-for="row in structure.quadrantRows"
                :key="row.key"
                class="structure-legend__item"
              >
                <span>
                  <i class="progress-row__dot" :style="{ background: row.color }" />
                  {{ row.name }}
                </span>
                <strong>{{ formatQty(row.value) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {
  outsourcingWarehouse_getInventoryStat,
  outsourcingWarehouse_listMaterialSummary,
} from "@/api/myController/outsourcing";
import {
  normalizeInventoryStatCards,
  normalizeInventoryStructure,
  buildInventoryStatPayload,
  buildMaterialSummaryPayload,
  normalizeMaterialSummaryList,
  buildMaterialSummaryChartOption,
  buildStructureDonutOption,
  formatInventoryQtyLabel,
} from "@/utils/outsourcingWarehouseSummary";

export default {
  name: "outsourcingProduct_warehouseSummary",
  data() {
    return {
      loading: false,
      statLoading: false,
      chartLoading: false,
      searchString: "",
      statCards: normalizeInventoryStatCards(),
      structure: normalizeInventoryStructure(),
      materialRows: [],
      materialTotal: 0,
      listQuery: {
        pageNum: 1,
        pageSize: 10,
      },
      chartHeight: 280,
      chartInstance: null,
      donutInstance: null,
    };
  },
  mounted() {
    this.refreshAll();
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.disposeCharts();
  },
  methods: {
    formatQty: formatInventoryQtyLabel,
    disposeCharts() {
      if (this.chartInstance) {
        this.chartInstance.dispose();
        this.chartInstance = null;
      }
      if (this.donutInstance) {
        this.donutInstance.dispose();
        this.donutInstance = null;
      }
    },
    refreshAll() {
      this.loading = true;
      Promise.all([this.loadStat(), this.loadMaterialSummary()]).finally(() => {
        this.loading = false;
      });
    },
    loadStat() {
      this.statLoading = true;
      return outsourcingWarehouse_getInventoryStat(
        buildInventoryStatPayload({ searchString: this.searchString })
      )
        .then((res) => {
          const data = res.data.data || {};
          this.statCards = normalizeInventoryStatCards(data);
          this.structure = normalizeInventoryStructure(data);
          this.$nextTick(() => this.renderDonut());
        })
        .catch(() => {
          this.statCards = normalizeInventoryStatCards({});
          this.structure = normalizeInventoryStructure({});
          this.$nextTick(() => this.renderDonut());
        })
        .finally(() => {
          this.statLoading = false;
        });
    },
    loadMaterialSummary() {
      this.chartLoading = true;
      return outsourcingWarehouse_listMaterialSummary(
        buildMaterialSummaryPayload({
          searchString: this.searchString,
          pageParam: this.listQuery,
        })
      )
        .then((res) => {
          const pageData = res.data.data || {};
          this.materialRows = normalizeMaterialSummaryList(
            pageData.dataList || []
          );
          this.materialTotal = pageData.totalCount || 0;
          this.$nextTick(() => this.renderChart());
        })
        .catch(() => {
          this.materialRows = [];
          this.materialTotal = 0;
          this.renderChart();
        })
        .finally(() => {
          this.chartLoading = false;
        });
    },
    renderChart() {
      if (!this.$refs.materialChart) return;
      if (!this.materialRows.length) {
        if (this.chartInstance) {
          this.chartInstance.clear();
        }
        return;
      }
      const { option, chartHeight } = buildMaterialSummaryChartOption(
        this.materialRows
      );
      this.chartHeight = chartHeight;
      this.$nextTick(() => {
        if (!this.chartInstance) {
          this.chartInstance = echarts.init(this.$refs.materialChart);
        }
        this.chartInstance.setOption(option, true);
        this.chartInstance.resize();
      });
    },
    renderDonut() {
      if (!this.$refs.structureDonut) return;
      const option = buildStructureDonutOption(
        this.structure.quadrantRows,
        "构成"
      );
      if (!this.donutInstance) {
        this.donutInstance = echarts.init(this.$refs.structureDonut);
      }
      this.donutInstance.setOption(option, true);
      this.donutInstance.resize();
    },
    handleResize() {
      if (this.chartInstance) this.chartInstance.resize();
      if (this.donutInstance) this.donutInstance.resize();
    },
    goOverview() {
      this.$router.push({ name: "outsourcingProduct_warehouseOverview" });
    },
    goWarehouse() {
      this.$router.push({ name: "outsourcingProduct_warehouse" });
    },
    goFlow() {
      this.$router.push({ name: "outsourcingProduct_warehouseFlow" });
    },
    goBatchInventory() {
      this.$router.push({
        name: "outsourcingProduct_warehouseBatchInventory",
      });
    },
  },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.warehouse-summary {
  padding-bottom: 24px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-header__title {
  margin: 0;
  color: #1476c3;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
}

.page-header__desc {
  margin: 6px 0 0;
  color: #909399;
  font-size: 13px;
}

.page-header__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.stat-panel {
  margin-bottom: 16px;
  padding: 16px 16px 0;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
}

.stat-card {
  position: relative;
  display: flex;
  min-height: 118px;
  margin-bottom: 16px;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #ebeef5;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.stat-card__accent {
  width: 4px;
  flex-shrink: 0;
}

.stat-card__body {
  flex: 1;
  min-width: 0;
  padding: 16px 18px;
}

.stat-card__title-row {
  margin-bottom: 10px;
}

.stat-card__title {
  color: #606266;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.stat-card__count {
  margin-bottom: 10px;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.stat-card__desc {
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.stat-card--primary {
  background: linear-gradient(135deg, #f5f9ff 0%, #ffffff 55%);
}

.stat-card--info {
  background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 55%);
}

.stat-card--warning {
  background: linear-gradient(135deg, #fff8ed 0%, #ffffff 55%);
}

.stat-card--success {
  background: linear-gradient(135deg, #f3fbf6 0%, #ffffff 55%);
}

.structure-row {
  margin-bottom: 8px;
}

.chart-panel,
.structure-panel {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px 20px 12px;
  min-height: 100%;
}

.chart-panel__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
}

.chart-panel__eyebrow {
  color: #909399;
  font-size: 12px;
  margin-bottom: 4px;
}

.chart-panel__title {
  color: #303133;
  font-size: 18px;
  font-weight: 700;
}

.chart-panel__body {
  width: 100%;
  min-height: 220px;
}

.chart-panel__empty {
  padding: 64px 0;
  color: #909399;
  text-align: center;
}

.chart-panel__pager {
  margin-top: 8px;
  overflow: hidden;
}

.structure-block {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #ebeef5;

  &:first-of-type {
    margin-top: 4px;
    padding-top: 0;
    border-top: none;
  }
}

.structure-block__title {
  margin-bottom: 10px;
  color: #303133;
  font-size: 13px;
  font-weight: 600;
}

.progress-row {
  margin-bottom: 12px;
}

.progress-row__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  color: #606266;
  font-size: 12px;
}

.progress-row__value {
  color: #303133;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.progress-row__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  border-radius: 50%;
  vertical-align: middle;
}

.structure-donut {
  width: 100%;
  height: 180px;
}

.structure-legend {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  margin-top: 4px;
}

.structure-legend__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: #f8fafc;
  border-radius: 6px;
  color: #606266;
  font-size: 12px;

  strong {
    color: #303133;
    font-variant-numeric: tabular-nums;
  }
}
</style>
